/**
 * @file EnrollmentReport.tsx
 * @brief Company report — voice enrollment readiness.
 *
 * @author DES
 * @date 27-07-2026
 */

import { useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { ReportTable } from "./ReportTable";
import { useEnrollmentReport } from "../api";
import { defaultReportRange } from "../defaultRange";
import { ReportShell } from "./ReportShell";

function displayName(row: {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}): string {
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
  return name || row.email || "—";
}

export function EnrollmentReportView() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultReportRange());
  const report = useEnrollmentReport();
  const summary = report.data?.summary;
  const rows = report.data?.employees ?? [];

  return (
    <ReportShell
      title="Enrollment readiness"
      description="Employees with or without Meeting Intel voice enrollment (current snapshot)."
      range={range}
      onRangeChange={setRange}
      showRangeBar={false}
      exportDisabled={rows.length === 0}
      serverExport={{
        scope: "company",
        reportKey: "enrollment",
      }}
    >
      <>
        <ReportKpiRow className="xl:grid-cols-2">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Enrolled"
              icon={Mic}
              value={String(summary?.enrolled ?? 0)}
              hint={`${summary?.enrolled_pct ?? 0}% of team`}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Not enrolled"
              icon={MicOff}
              value={String(summary?.not_enrolled ?? 0)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Employees"
          description="Not-enrolled employees listed first for follow-up."
        />
        <ReportTableCell>
          <ReportTable
            headers={["Employee", "Role", "Enrollment"]}
            isLoading={report.isLoading}
            empty={rows.length === 0}
          >
            {rows.map((row) => (
              <tr key={row.user_id} className="hover:bg-secondary/20">
                <td className="px-4 py-2">
                  <div className="font-medium">{displayName(row)}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.email}
                  </div>
                </td>
                <td className="px-4 py-2 capitalize text-muted-foreground">
                  {row.role || "—"}
                </td>
                <td className="px-4 py-2 text-right">
                  {row.enrolled ? (
                    <Badge variant="success">Enrolled</Badge>
                  ) : (
                    <Badge variant="warning">Not enrolled</Badge>
                  )}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
